'use client';

import { Construction } from 'lucide-react';
import Link from 'next/link';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

type SimpleOopsFallbackProps = {
  className?: string;
  subtitle?: string;
  onRetry?: () => void;
  retryLabel?: string;
  extra?: ReactNode;
};

const btnPrimary =
  'inline-flex h-10 items-center justify-center rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-primary-foreground shadow ring-offset-background transition-colors hover:bg-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const btnGhost =
  'inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export function SimpleOopsFallback({
  className,
  subtitle,
  onRetry,
  retryLabel = 'Try again',
  extra,
}: SimpleOopsFallbackProps) {
  return (
    <div
      className={cn(
        'flex min-h-[100dvh] flex-col items-center justify-center gap-6 bg-background px-6 py-12 text-center',
        className,
      )}
    >
      <div
        className='flex size-16 items-center justify-center rounded-full bg-muted'
        aria-hidden
      >
        <Construction
          className='size-8 text-muted-foreground'
          strokeWidth={1.5}
        />
      </div>
      <div className='max-w-sm space-y-2'>
        <h1 className='text-lg font-medium tracking-tight text-foreground sm:text-xl'>
          Oops, we&apos;re working on it.
        </h1>
        <p className='text-sm text-muted-foreground'>Please refresh the page</p>
        {subtitle ? (
          <p className='text-sm text-muted-foreground'>{subtitle}</p>
        ) : null}
      </div>
      <div className='flex flex-col items-center gap-3 sm:flex-row sm:gap-4'>
        {onRetry ? (
          <button type='button' onClick={onRetry} className={btnPrimary}>
            {retryLabel}
          </button>
        ) : null}
        {extra ?? (
          <Link href='/' className={cn(btnGhost)}>
            Go home
          </Link>
        )}
      </div>
    </div>
  );
}
